import React, { PropsWithChildren, useEffect, useRef } from 'react';
import { domain } from './configuration/Environment';
import { normalize, normalizeAuthHeader } from './utils/stringUtils';
import { Hooks } from '@3nickels/data-modules';

export type AuditCollectorProps = PropsWithChildren;

const isDevBuild = process.env.NODE_ENV !== 'production';

export const AuditCollector: React.FC<AuditCollectorProps> = ({ children }) => {
  const hasLoggedMissingTokenRef = useRef(false);
  const initializedRef = useRef(false);
  const { accountId } = Hooks.usePersonData() ?? {};

  useEffect(() => {
    if (!accountId) return;
    // Dynamically import to keep @embrace-io out of the critical bundle
    import('@embrace-io/web-sdk')
      .then(({ user }) => {
        user.setUserId(accountId.toString());
      })
      .catch(() => {});
  }, [accountId]);

  useEffect(() => {
    if (initializedRef.current) return;

    const rawToken = normalize(process.env.REACT_APP_AUDIT_COLLECTION_AUTH_TOKEN);
    const authorizationHeader = normalizeAuthHeader(rawToken);
    if (!authorizationHeader) {
      if (!hasLoggedMissingTokenRef.current) {
        hasLoggedMissingTokenRef.current = true;
        console.log('[AuditCollector] Embrace initialization skipped: missing or invalid token');
      }
      return;
    }

    initializedRef.current = true;

    // Lazy-load @embrace-io/web-sdk, @opentelemetry exporters, and the react instrumentation.
    // These are heavy dependencies (~54 MB disk) that are only needed for telemetry and
    // should not block the critical rendering path.
    Promise.all([
      import('@embrace-io/web-sdk'),
      import('@embrace-io/web-sdk/react-instrumentation'),
      import('@opentelemetry/sdk-logs'),
      import('@opentelemetry/exporter-logs-otlp-http'),
      import('@opentelemetry/exporter-trace-otlp-http'),
    ])
      .then(
        ([
          { DiagLogLevel, initSDK },
          { createReactRouterNavigationInstrumentation },
          { ConsoleLogRecordExporter, SimpleLogRecordProcessor },
          { OTLPLogExporter },
          { OTLPTraceExporter },
        ]) => {
          const collectorBaseUrl = `https://collector.${domain}`;
          const traceUrl = `${collectorBaseUrl}/v1/traces`;
          const logUrl = `${collectorBaseUrl}/v1/logs`;
          const ignoreUrls = [traceUrl, logUrl];

          const logProcessors = isDevBuild
            ? [new SimpleLogRecordProcessor(new ConsoleLogRecordExporter())]
            : [];

          initSDK({
            logLevel: DiagLogLevel.ALL,
            spanExporters: [
              new OTLPTraceExporter({
                url: traceUrl,
                headers: { Authorization: authorizationHeader },
              }),
            ],
            logExporters: [
              new OTLPLogExporter({
                url: logUrl,
                headers: { Authorization: authorizationHeader },
              }),
            ],
            logProcessors,
            defaultInstrumentationConfig: {
              network: { ignoreUrls },
            },
            instrumentations: [createReactRouterNavigationInstrumentation()],
          });
        }
      )
      .catch((error) => {
        console.error('[AuditCollector] Failed to initialize Embrace SDK', error);
      });
  }, []);

  return <>{children}</>;
};
